/**
 * Step-up re-authentication — awaitable wrapper for any async operation
 * that may be rejected by the backend with `step_up_required`.
 *
 * Design notes:
 *
 *   1. NOT a React provider. The "state" of a pending step-up
 *      challenge lives in a Zustand store — any component (a
 *      mutation, a route loader, a command-palette action) can
 *      trigger it without having to be inside a provider.
 *
 *   2. One modal, mounted once. `<StepUpDialog />` is rendered at the
 *      app root and subscribes to the store. When `withStepUp()`
 *      detects a `StepUpRequiredError`, it pushes the challenge into
 *      the store, the dialog opens, and the caller `await`s a
 *      promise that resolves when the ceremony completes (or
 *      rejects if the user cancels).
 *
 *   3. The wrapper is idempotent per call: if the retried request
 *      ALSO throws `StepUpRequiredError`, we bubble it up rather
 *      than loop. The loop protection matters because a buggy policy
 *      change on the server could otherwise trap the user in a
 *      forever ceremony.
 *
 *   4. Cancellation is explicit. Closing the dialog rejects the
 *      promise with `StepUpCancelledError`; the calling mutation
 *      receives the error through TanStack Query's normal error
 *      channel and can decide whether to roll back optimistic state.
 *
 * Usage from a mutation:
 *
 *     const deleteTenant = useMutation({
 *       mutationFn: (slug: string) =>
 *         withStepUp(() => apiFetch(`/api/admin/v1/tenants/${slug}`, { method: "DELETE" })),
 *     });
 */

import { create } from "zustand";
import { StepUpRequiredError } from "./api-client";

// ── Errors ────────────────────────────────────────────────────────────────

export class StepUpCancelledError extends Error {
  constructor() {
    super("Step-up verification was cancelled by the user.");
    this.name = "StepUpCancelledError";
  }
}

// ── Store ─────────────────────────────────────────────────────────────────

/**
 * The store holds at most one pending challenge at a time. We don't
 * queue — a second step-up request arriving while a ceremony is
 * already in progress is a bug, and we'd rather surface it than
 * silently serialize unrelated operations.
 */
interface PendingChallenge {
  reason: string;
  resolve: () => void;
  reject: (err: Error) => void;
}

interface StepUpStore {
  pending: PendingChallenge | null;
  /** Open the dialog with a new challenge. Internal — called by withStepUp. */
  open: (challenge: Omit<PendingChallenge, "resolve" | "reject">) => Promise<void>;
  /** Called by the dialog when the WebAuthn ceremony succeeds. */
  markVerified: () => void;
  /** Called by the dialog when the user cancels. */
  cancel: () => void;
  /** Called by the dialog when the ceremony itself 401s — the
   *  user's session died between the original 403 and this
   *  ceremony. Rejects the pending promise with the supplied
   *  error (typically AuthError) so the caller bubbles it up
   *  and the QueryClient's throwOnError fires the global
   *  SignedOutScreen. */
  failPending: (err: Error) => void;
}

export const useStepUpStore = create<StepUpStore>((set, get) => ({
  pending: null,

  open: (challenge) => {
    if (get().pending !== null) {
      // Another ceremony is already in flight. This should not happen
      // in a well-behaved app — throwing surfaces the bug.
      return Promise.reject(
        new Error("step-up already in progress; concurrent ceremonies are not supported"),
      );
    }
    return new Promise<void>((resolve, reject) => {
      set({
        pending: {
          reason: challenge.reason,
          resolve,
          reject,
        },
      });
    });
  },

  markVerified: () => {
    const pending = get().pending;
    if (!pending) return;
    set({ pending: null });
    pending.resolve();
  },

  cancel: () => {
    const pending = get().pending;
    if (!pending) return;
    set({ pending: null });
    pending.reject(new StepUpCancelledError());
  },

  failPending: (err) => {
    const pending = get().pending;
    if (!pending) return;
    set({ pending: null });
    pending.reject(err);
  },
}));

// ── withStepUp wrapper ────────────────────────────────────────────────────

/**
 * Run `task`. If it throws `StepUpRequiredError`, surface the ceremony
 * to the user via the Zustand-backed dialog, wait for it to succeed,
 * and re-run `task` exactly once.
 *
 * The retry is single-shot by design. If the server is still
 * demanding step-up on the second attempt, something is wrong —
 * probably a policy that cannot be satisfied by the current session —
 * and we surface the error instead of looping.
 */
export async function withStepUp<T>(task: () => Promise<T>): Promise<T> {
  try {
    return await task();
  } catch (err) {
    if (!(err instanceof StepUpRequiredError)) throw err;

    await useStepUpStore.getState().open({
      reason: err.reason,
    });

    // User completed the ceremony. Retry exactly once.
    return await task();
  }
}
